import { Group, Modal, Select, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { uniqBy } from 'lodash';
import { useTemplateEditorData } from 'pages/revisions-module/template-editor/TemplateEditorDataProvider';
import { GOOGLE_FONTS } from 'pages/revisions-module/template-editor/editors/visual/typography/google-fonts.generated';
import {
  shouldLoadGoogleFont,
  toGoogleFontHref,
} from 'pages/revisions-module/template-editor/editors/visual/typography/google-font-utils';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import Button from 'components/Button';

interface OpenGoogleFontsSettingsParams {
  onClose?: () => void;
}

interface IGoogleFontsSettingsContext {
  openGoogleFontsSettings: (params: OpenGoogleFontsSettingsParams) => void;
}

const GoogleFontsSettingsContext = createContext<IGoogleFontsSettingsContext>(undefined!);

/**
 * Converts Google font name into CSS font-family value.
 *
 * @param fontName Google font name.
 * @returns CSS font family string with fallback.
 */
function toCssFontFamily(fontName: string) {
  return `'${fontName}', sans-serif`;
}

/**
 * Content of the modal used to load Google Fonts into visual settings.
 */
function GoogleFontsModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const { visual, setVisual } = useTemplateEditorData();
  const [selectedGoogleFont, setSelectedGoogleFont] = useState<string | null>(null);

  const options = useMemo(
    () =>
      GOOGLE_FONTS.filter(
        (fontName) => !(visual.googleFonts ?? []).some((googleFont) => googleFont.fontName === fontName)
      ),
    [visual.googleFonts]
  );

  /**
   * Persists selected Google font into visual settings.
   */
  const addGoogleFont = useCallback(() => {
    if (!selectedGoogleFont) {
      /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */
      return;
    }

    setVisual((prevVisual) => ({
      ...prevVisual,
      googleFonts: uniqBy(
        [
          ...(prevVisual.googleFonts ?? []),
          {
            fontName: selectedGoogleFont,
            href: toGoogleFontHref(selectedGoogleFont),
          },
        ],
        (googleFont) => googleFont.fontName
      ),
    }));

    setSelectedGoogleFont(null);
    onClose();
  }, [onClose, selectedGoogleFont, setVisual]);

  return (
    <Modal opened={opened} onClose={onClose} title="Pridať Google Font" size={460} centered>
      {selectedGoogleFont && shouldLoadGoogleFont(selectedGoogleFont) && (
        <link rel="stylesheet" href={toGoogleFontHref(selectedGoogleFont)} />
      )}

      <Stack spacing={14}>
        <Select
          label="Vyberte Google font"
          placeholder="Napr. Roboto"
          searchable
          nothingFound="Nič sa nenašlo"
          data={options}
          value={selectedGoogleFont}
          onChange={setSelectedGoogleFont}
        />

        {selectedGoogleFont ? (
          <Text style={{ fontFamily: toCssFontFamily(selectedGoogleFont) }}>
            Ukážka: Revízia vykonaná v súlade s STN 33 1500 a STN 34 1390
          </Text>
        ) : null}

        <Group position="right" mt={8}>
          <Button variant="subtle-gray" onClick={onClose}>
            Zrušiť
          </Button>
          <Button variant="primary" onClick={addGoogleFont} disabled={!selectedGoogleFont}>
            Pridať font
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * Provides modal and actions for loading Google Fonts.
 */
export function GoogleFontsSettingsProvider({ children }: { children: React.ReactNode }) {
  const [opened, { open, close }] = useDisclosure(false);
  const [params, setParams] = useState<OpenGoogleFontsSettingsParams>({});

  /**
   * Opens the Google Fonts modal.
   *
   * @param params Optional callbacks for lifecycle hooks.
   */
  const openGoogleFontsSettings = useCallback(
    (params: OpenGoogleFontsSettingsParams) => {
      setParams(params);
      open();
    },
    [open]
  );

  /**
   * Closes the Google Fonts modal and runs `onClose` callback.
   */
  const onClose = useCallback(() => {
    params.onClose?.();
    close();
  }, [params, close]);

  const value = useMemo(() => ({ openGoogleFontsSettings }), [openGoogleFontsSettings]);

  return (
    <GoogleFontsSettingsContext.Provider value={value}>
      {children}
      <GoogleFontsModal opened={opened} onClose={onClose} />
    </GoogleFontsSettingsContext.Provider>
  );
}

/**
 * Hook for opening Google Fonts modal.
 */
export function useGoogleFontsSettings() {
  return useContext(GoogleFontsSettingsContext);
}
